import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { ReactComponent as ShareIcon } from '../../../icons/share.svg';
import './share-project-app-button.scss';

interface ShareProjectAppButtonProps {
  appId: string;
}

const ShareProjectAppButton: React.FC<ShareProjectAppButtonProps> = ({
  appId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [isShared, setIsShared] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const shareUrl = shareToken
    ? `${window.location.origin}/shared/${shareToken}`
    : '';

  // Fetch share status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get(`/project-apps/${appId}/share-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.success) {
          setIsShared(res.data.isShared);
          setShareToken(res.data.shareToken || null);
        }
      } catch (err) {
        console.error('Error fetching share status:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [appId, getAccessTokenSilently]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const handleShare = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post(
        `/project-apps/${appId}/share`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        setIsShared(true);
        setShareToken(res.data.shareToken);
        setShowDropdown(true);
      }
    } catch (err) {
      console.error('Error sharing project app:', err);
    }
  }, [appId, getAccessTokenSilently]);

  const handleUnshare = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await api.delete(`/project-apps/${appId}/share`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsShared(false);
      setShareToken(null);
      setShowDropdown(false);
    } catch (err) {
      console.error('Error unsharing project app:', err);
    }
  }, [appId, getAccessTokenSilently]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  }, [shareUrl]);

  if (loading) return null;

  return (
    <div className='share-project-app-button' ref={dropdownRef}>
      <button
        className={`share-btn${isShared ? ' shared' : ''}`}
        onClick={isShared ? () => setShowDropdown(!showDropdown) : handleShare}
        title={isShared ? 'Manage sharing' : 'Share this page'}
      >
        <ShareIcon />
        <span>{isShared ? 'Shared' : 'Share'}</span>
      </button>

      {showDropdown && isShared && (
        <div className='share-dropdown'>
          <div className='share-dropdown-header'>
            <span>Anyone with the link can view</span>
          </div>
          <div className='share-dropdown-url'>
            <input type='text' value={shareUrl} readOnly />
            <button className='copy-btn' onClick={handleCopy}>
              {copying ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <button className='unshare-btn' onClick={handleUnshare}>
            Stop sharing
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareProjectAppButton;
